import { useState, useCallback, type ReactNode } from 'react';
import type { EvinAnalysisResult, PlanFeatures } from '../../../shared/types';
import { PLAN_FEATURES, PRICING_TIERS } from '../../../shared/types';
import { buildAnalysisEmailHtml } from '../../services/emailHtml';
import { downloadAnalysisPdf, pdfFilenameFor } from '../../services/pdfExport';
import { useAuth } from '../../contexts/AuthContext';
import { copyRich, markdownToHtml, markdownToPlainText } from '../../utils/markdown';
import Modal from '../ui/Modal';

/* Barre d'actions (copier + PDF + email + WhatsApp + éditer) avec sa pop-up
 * email. Extrait tel quel de ResultsDisplay.tsx ; la modale email utilise
 * désormais la coquille partagée <Modal> (DOM identique).
 *
 * Chaque bouton est adossé à SA feature de plan (copyText / exportPdf /
 * shareEmail / shareWhatsapp) : la barre ne se jouait auparavant que sur
 * `copyText`, ce qui offrait le partage email et WhatsApp à un abonné Pro
 * alors que la grille tarifaire les réserve au Premium.
 *
 * `showLocked` : la barre globale du bas montre les actions verrouillées en
 * grisé plutôt que de les escamoter — un visiteur venu d'un code promo doit
 * voir ce que débloque l'abonnement. Les barres de survol, elles, restent
 * silencieuses (rien d'actionnable = rien à afficher). */

/** Premier plan de la grille tarifaire qui débloque cette feature. */
function lockedTitle(feature: keyof PlanFeatures, label: string): string {
  const tier = PRICING_TIERS.find((t) => PLAN_FEATURES[t.plan][feature] === true);
  return tier ? `${label} — réservé au plan ${tier.name}` : `${label} — indisponible avec votre plan`;
}

const BTN = "p-1.5 rounded-lg text-slate-400 dark:text-slate-500 shader:text-white/40 hover:text-slate-700 dark:hover:text-white shader:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-wait";
const BTN_LOCKED = "relative p-1.5 rounded-lg text-slate-300 dark:text-slate-600 shader:text-white/20 pastel:text-(--p-ink)/25 cursor-not-allowed";

/** Un bouton d'action, grisé (ou escamoté) quand le plan ne l'inclut pas.
 *  Déclaré hors du composant : une définition dans le corps du rendu change
 *  d'identité à chaque passe et ferait remonter les boutons. */
function Action({ feature, features, showLocked, label, onClick, disabled, children }: {
  feature: keyof PlanFeatures;
  features: PlanFeatures;
  showLocked: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  if (features[feature] !== true) {
    if (!showLocked) return null;
    return (
      <button type="button" disabled title={lockedTitle(feature, label)} className={BTN_LOCKED}>
        {children}
        <svg className="absolute bottom-0 right-0 w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V11a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm0 2a3 3 0 0 1 3 3v3H9V6a3 3 0 0 1 3-3z" />
        </svg>
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={label} className={BTN}>
      {children}
    </button>
  );
}

export default function ActionBar({ text, onEdit, result, showLocked = false }: { text: string; onEdit?: () => void; result?: EvinAnalysisResult; showLocked?: boolean }) {
  const { features } = useAuth();
  const [copied, setCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const unlocked = (feature: keyof PlanFeatures) => features[feature] === true;

  const handleCopy = useCallback(async () => {
    await copyRich(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  // Copie le rapport (HTML + texte) puis ouvre le client mail. Déclenché depuis
  // la pop-up explicative — l'utilisateur sait qu'il devra coller le contenu.
  const sendEmail = useCallback(async () => {
    const html = result ? buildAnalysisEmailHtml(result) : markdownToHtml(text);
    const plain = markdownToPlainText(text);
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
    } catch {
      await navigator.clipboard.writeText(plain);
    }
    setShowEmailModal(false);
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 4000);
    window.open(`mailto:?subject=${encodeURIComponent('Analyse Evinly')}`);
  }, [text, result]);

  const handlePdf = useCallback(async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      await downloadAnalysisPdf(result, pdfFilenameFor(result.originalInput));
    } finally {
      setPdfLoading(false);
    }
  }, [result]);

  const handleWhatsApp = useCallback(() => {
    window.open(`https://wa.me/?text=${encodeURIComponent('*Analyse Evinly*\n\n' + markdownToPlainText(text))}`, '_blank');
  }, [text]);

  // Rien d'actionnable et rien à montrer : la barre disparaît (comportement
  // historique des barres de survol sur le plan free). Ce retour anticipé
  // vient APRÈS les hooks — les placer sous un `return` conditionnel change
  // leur nombre d'un rendu à l'autre dès que le plan évolue.
  const anyUnlocked = unlocked('copyText') || unlocked('exportPdf')
    || unlocked('shareEmail') || unlocked('shareWhatsapp');
  if (!showLocked && !anyUnlocked) return null;


  return (
    <div className="relative flex items-center justify-center gap-1 py-1.5">
      <Action features={features} showLocked={showLocked} feature="copyText" label={copied ? 'Copié !' : 'Copier'} onClick={handleCopy}>
        {copied
          ? <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><polyline points="20 6 9 17 4 12" /></svg>
          : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>}
      </Action>
      {/* Téléchargement PDF — fichier prêt à joindre en pièce jointe */}
      {result && (
        <Action features={features} showLocked={showLocked} feature="exportPdf" label="Télécharger en PDF" onClick={handlePdf} disabled={pdfLoading}>
          {pdfLoading
            ? <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" /></svg>
            : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></svg>}
        </Action>
      )}
      {/* Email — ouvre une pop-up explicative (le rapport se colle manuellement) */}
      <Action features={features} showLocked={showLocked} feature="shareEmail" label="Envoyer par email" onClick={() => setShowEmailModal(true)}>
        {emailCopied
          ? <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><polyline points="20 6 9 17 4 12" /></svg>
          : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>}
      </Action>
      <Action features={features} showLocked={showLocked} feature="shareWhatsapp" label="WhatsApp" onClick={handleWhatsApp}>
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9L3 21" /><path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" /></svg>
      </Action>
      {onEdit && (
        // L'édition suit `copyText` : sortir le texte par un textarea
        // contournerait la protection anti-copie posée sur le résultat.
        <Action features={features} showLocked={showLocked} feature="copyText" label="Modifier" onClick={onEdit}>
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
        </Action>
      )}
      {emailCopied && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-lg bg-slate-900 dark:bg-white shader:bg-white text-white dark:text-slate-900 shader:text-slate-900 text-xs shadow-lg animate-fade-in">
          Rapport copié — collez-le (Ctrl+V) dans votre email
        </div>
      )}

      {showEmailModal && (
        <Modal
          onClose={() => setShowEmailModal(false)}
          className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 shader:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl p-6 text-left"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 shrink-0">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
            </span>
            <h3 className="font-display text-lg text-slate-900 dark:text-white">Envoyer le rapport par email</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
            Votre messagerie va s'ouvrir avec un nouveau message vide. Le rapport complet, mis en forme, est <strong>copié dans votre presse-papier</strong>.
          </p>
          <div className="rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-100 dark:border-violet-800/40 px-4 py-3 mb-4">
            <p className="text-sm text-violet-800 dark:text-violet-200 leading-relaxed">
              Une fois le message ouvert, <strong>collez le rapport</strong> avec <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-violet-200 dark:border-slate-600 text-xs font-mono">Ctrl</kbd>+<kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-violet-200 dark:border-slate-600 text-xs font-mono">V</kbd> (ou <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-violet-200 dark:border-slate-600 text-xs font-mono">Cmd</kbd>+<kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-700 border border-violet-200 dark:border-slate-600 text-xs font-mono">V</kbd> sur Mac), ou via <em>Édition ▸ Coller</em>.
            </p>
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500 mb-5">
            Astuce : pour envoyer le rapport en pièce jointe, utilisez plutôt le bouton « Télécharger en PDF ».
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowEmailModal(false)}
              className="font-display px-4 py-2 rounded-lg text-sm border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={sendEmail}
              className="font-display px-4 py-2 rounded-lg text-sm text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 transition-colors"
            >
              Ouvrir le client mail
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
